/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import API_BASE_URL from "../config";

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL  = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function AdminDashboard() {
  const now = new Date();

  const [activeMonth,    setActiveMonth]    = useState(`${MONTHS_SHORT[now.getMonth()]}-${now.getFullYear()}`);
  const [monthData,      setMonthData]      = useState({});
  const [scheduleConfig, setScheduleConfig] = useState({});
  const [loading,        setLoading]        = useState(false);

  const [filterVehicle, setFilterVehicle]   = useState('');
  const [filterVType,   setFilterVType]     = useState('');
  const [filterMonth,   setFilterMonth]     = useState('');
  const [filterYear,    setFilterYear]      = useState(String(now.getFullYear()));
  const [filterStatus,  setFilterStatus]    = useState('');
  const [acOpen,        setAcOpen]          = useState(false);
  const [acMatches,     setAcMatches]       = useState([]);

  const [schedDate,      setSchedDate]      = useState(now.toISOString().split('T')[0]);
  const [schedPopup,     setSchedPopup]     = useState('');

  const [selectedDate,   setSelectedDate]   = useState('');
  const [dailyCollapsed, setDailyCollapsed] = useState(false);

  const [tpsCollapsed,   setTpsCollapsed]   = useState(false);
  const [tpsSearch,      setTpsSearch]      = useState('');
  const [tpsSortCol,     setTpsSortCol]     = useState('vehicle');
  const [tpsSortDir,     setTpsSortDir]     = useState('asc');

  const [pvmileOpen,     setPvmileOpen]     = useState(false);
  const [pvmileSearch,   setPvmileSearch]   = useState('');

  const [dlOpen,         setDlOpen]         = useState(false);

  /** Vehicle onboarding records (Vehicle Management) — not hardcoded */
  const [fleetRecords, setFleetRecords]   = useState([]);
  /** Driver onboarding records (Driver Management) */
  const [driversList, setDriversList]     = useState([]);
  const [fleetLoading, setFleetLoading]   = useState(true);

  useEffect(() => { loadMonthData(activeMonth); }, [activeMonth]);
  useEffect(() => { loadScheduleConfig(); }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setFleetLoading(true);
      let vehicles = [];
      let drivers = [];
      if (!API_BASE_URL) {
        if (!cancelled) {
          setFleetRecords([]);
          setDriversList([]);
          setFleetLoading(false);
        }
        return;
      }
      try {
        const vRes = await axios.get(`${API_BASE_URL}/onboarding/all-vehicles`);
        vehicles = Array.isArray(vRes.data) ? vRes.data : [];
      } catch (e) {
        if (e.response?.status !== 404) console.warn('AdminDashboard: vehicles', e.message);
      }
      try {
        const dRes = await axios.get(`${API_BASE_URL}/onboarding/drivers`);
        drivers = Array.isArray(dRes.data) ? dRes.data : [];
      } catch (e) {
        try {
          const d2 = await axios.get(`${API_BASE_URL}/onboarding/driver`);
          drivers = Array.isArray(d2.data) ? d2.data : [];
        } catch (e2) {
          if (e.response?.status !== 404 && e2.response?.status !== 404) {
            console.warn('AdminDashboard: drivers', e2.message);
          }
        }
      }
      if (!cancelled) {
        setFleetRecords(vehicles);
        setDriversList(drivers);
        setFleetLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const loadMonthData = async (monthKey) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/vehicle/diesel/by-month/${encodeURIComponent(monthKey)}`);
      setMonthData(res.data?.data || {});
    } catch { setMonthData({}); }
    setLoading(false);
  };

  const loadScheduleConfig = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/schedule`);
      const cfg = res.data?.data || {};
      const local = localStorage.getItem('fleetScheduleConfig');
      if (local) Object.assign(cfg, JSON.parse(local));
      setScheduleConfig(cfg);
    } catch {
      try {
        const local = localStorage.getItem('fleetScheduleConfig');
        if (local) setScheduleConfig(JSON.parse(local));
      } catch {}
    }
  };

  const [aMon, aYr] = activeMonth.split('-');
  const monIdx      = MONTHS_SHORT.indexOf(aMon);
  const daysInMon   = new Date(parseInt(aYr, 10), monIdx + 1, 0).getDate();
  const allMonthDates = useMemo(() =>
    Array.from({ length: daysInMon }, (_, i) =>
      `${String(i + 1).padStart(2, '0')}-${aMon}`
    ), [daysInMon, aMon]);

  /** Union: vehicles from onboarding + any vehicle keys present in fuel data */
  const vehicleNumbers = useMemo(() => {
    const fromApi = fleetRecords.map(v => v.vehicleNumber).filter(Boolean);
    const fromFills = Object.keys(monthData || {});
    const uniq = new Set([...fromApi, ...fromFills]);
    return Array.from(uniq).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [fleetRecords, monthData]);

  const vehicleMeta = useMemo(() => {
    const m = {};
    fleetRecords.forEach(v => {
      if (!v.vehicleNumber) return;
      m[v.vehicleNumber] = {
        vehicleType: v.vehicleType || '',
        registerName: v.registerName || '',
      };
    });
    return m;
  }, [fleetRecords]);

  /** Merge API/local schedule with per-vehicle Mongo schedule fields */
  const effectiveScheduleConfig = useMemo(() => {
    const out = { ...scheduleConfig };
    fleetRecords.forEach(v => {
      const num = v.vehicleNumber;
      if (!num) return;
      const interval = v.scheduleInterval;
      if (interval != null && interval !== '' && Number(interval) > 0) {
        out[num] = {
          ...out[num],
          interval: Number(interval),
          kmPerLitre: v.scheduleKmPerLitre != null && v.scheduleKmPerLitre !== ''
            ? Number(v.scheduleKmPerLitre)
            : out[num]?.kmPerLitre,
          litresPerFill: v.scheduleLitres != null && v.scheduleLitres !== ''
            ? Number(v.scheduleLitres)
            : out[num]?.litresPerFill,
        };
      }
    });
    return out;
  }, [scheduleConfig, fleetRecords]);

  const getVehicleTypeDisplay = (vehicleNo) => {
    const t = vehicleMeta[vehicleNo]?.vehicleType;
    if (t) return t;
    if (vehicleNo.startsWith('NEW')) return vehicleNo.includes('17FT') ? '17FT' : vehicleNo.includes('20FT') ? '20FT' : 'New';
    return '';
  };

  const dateTotals = useMemo(() => {
    const dt = {};
    allMonthDates.forEach(d => { dt[d] = { l: 0, a: 0, count: 0 }; });
    vehicleNumbers.forEach(v => {
      const vd = monthData[v] || {};
      Object.entries(vd).forEach(([date, info]) => {
        if (!dt[date]) dt[date] = { l: 0, a: 0, count: 0 };
        dt[date].l += info.l || 0;
        dt[date].a += info.a || 0;
        dt[date].count += 1;
      });
    });
    return dt;
  }, [allMonthDates, monthData, vehicleNumbers]);

  let totalLitres = 0, totalAmount = 0, totalFills = 0;
  Object.values(dateTotals).forEach(dt => {
    totalLitres += dt.l;
    totalAmount += dt.a;
    totalFills  += dt.count;
  });

  const hasAnyData = totalFills > 0;

  useEffect(() => {
    const datesWithData = Object.keys(dateTotals).filter(d => dateTotals[d]?.count > 0);
    if (datesWithData.length > 0) {
      datesWithData.sort((a, b) => {
        const [da, ma] = a.split('-'), [db, mb] = b.split('-');
        return MONTHS_SHORT.indexOf(ma) - MONTHS_SHORT.indexOf(mb) || parseInt(da) - parseInt(db);
      });
      setSelectedDate(datesWithData[datesWithData.length - 1]);
    } else {
      setSelectedDate(allMonthDates[0] || '');
    }
  }, [activeMonth, monthData, dateTotals, allMonthDates]);

  /** Month key for schedule date picker (diesel month slice may differ from dashboard month filter). */
  const schedMonthKey = useMemo(() => {
    if (!schedDate) return '';
    const [y, m] = schedDate.split('-');
    return `${MONTHS_SHORT[parseInt(m, 10) - 1]}-${y}`;
  }, [schedDate]);

  const [schedMonthData, setSchedMonthData] = useState({});

  useEffect(() => {
    if (!API_BASE_URL || !schedMonthKey) return;
    if (schedMonthKey === activeMonth) return;
    let cancelled = false;
    axios.get(`${API_BASE_URL}/vehicle/diesel/by-month/${encodeURIComponent(schedMonthKey)}`)
      .then(res => { if (!cancelled) setSchedMonthData(res.data?.data || {}); })
      .catch(() => { if (!cancelled) setSchedMonthData({}); });
    return () => { cancelled = true; };
  }, [schedMonthKey, activeMonth]);

  const scheduleData = schedMonthKey === activeMonth ? monthData : schedMonthData;

  const getSchedVehicles = () => vehicleNumbers.filter(v => effectiveScheduleConfig[v]?.interval);

  const getFilledForDate = (dateStr, data = monthData) => {
    const [y, m, d] = dateStr.split('-');
    const key = `${d}-${MONTHS_SHORT[parseInt(m, 10) - 1]}`;
    return getSchedVehicles().filter(v => (data[v] || {})[key]);
  };

  const getUnfilledForDate = (dateStr, data = monthData) => {
    const filled = new Set(getFilledForDate(dateStr, data).map(v => v));
    return getSchedVehicles().filter(v => !filled.has(v));
  };

  const getDayMileage = (d) => {
    let expKm = 0, actKm = 0, litres = 0;
    vehicleNumbers.forEach(v => {
      const info = (monthData[v] || {})[d];
      if (!info) return;
      const sc = effectiveScheduleConfig[v];
      if (sc?.kmPerLitre) expKm += Math.round(sc.kmPerLitre * info.l);
      litres += info.l;
    });
    return { expKm, actKm, litres, kml: (actKm > 0 && litres > 0) ? (actKm / litres).toFixed(1) : null };
  };

  const getFilteredVehicles = () => {
    return vehicleNumbers.filter(v => {
      if (tpsSearch && !v.toLowerCase().includes(tpsSearch.toLowerCase())) return false;
      if (filterVehicle && !v.toLowerCase().includes(filterVehicle.toLowerCase())) return false;
      if (filterVType) {
        const t = getVehicleTypeDisplay(v);
        if (t !== filterVType && !(filterVType === 'Other' && !t)) return false;
      }
      const fills = Object.keys(monthData[v] || {}).length;
      if (filterStatus === 'active'   && fills === 0) return false;
      if (filterStatus === 'inactive' && fills > 0) return false;
      return true;
    });
  };

  const sortedTpsVehicles = [...getFilteredVehicles()].sort((a, b) => {
    const ftA = { fills: Object.keys(monthData[a] || {}).length, l: Object.values(monthData[a] || {}).reduce((s, i) => s + (i.l || 0), 0), a: Object.values(monthData[a] || {}).reduce((s, i) => s + (i.a || 0), 0) };
    const ftB = { fills: Object.keys(monthData[b] || {}).length, l: Object.values(monthData[b] || {}).reduce((s, i) => s + (i.l || 0), 0), a: Object.values(monthData[b] || {}).reduce((s, i) => s + (i.a || 0), 0) };
    let va, vb;
    switch (tpsSortCol) {
      case 'vehicle': va = a; vb = b; break;
      case 'vtype':   va = getVehicleTypeDisplay(a); vb = getVehicleTypeDisplay(b); break;
      case 'fills':   va = ftA.fills; vb = ftB.fills; break;
      case 'litres':  va = ftA.l;     vb = ftB.l;     break;
      case 'amount':  va = ftA.a;     vb = ftB.a;     break;
      case 'avg':     va = ftA.fills > 0 ? ftA.l / ftA.fills : 0; vb = ftB.fills > 0 ? ftB.l / ftB.fills : 0; break;
      default:        va = a; vb = b;
    }
    if (va < vb) return tpsSortDir === 'asc' ? -1 : 1;
    if (va > vb) return tpsSortDir === 'asc' ?  1 : -1;
    return 0;
  });

  const handleTpsSort = (col) => {
    if (tpsSortCol === col) setTpsSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setTpsSortCol(col); setTpsSortDir(col === 'vehicle' || col === 'vtype' ? 'asc' : 'desc'); }
  };

  const classifyFillDay = (v, d) => {
    const sc = effectiveScheduleConfig[v];
    if (!sc?.interval) return 'none';
    const interval = sc.interval;
    const allFillDates = Object.keys(monthData[v] || {}).sort();
    const idx = allFillDates.indexOf(d);
    if (idx <= 0) return 'sched';
    const prev = allFillDates[idx - 1];
    const [da, ma] = d.split('-'); const [dp, mp] = prev.split('-');
    const dateA = new Date(parseInt(aYr), MONTHS_SHORT.indexOf(ma), parseInt(da));
    const dateB = new Date(parseInt(aYr), MONTHS_SHORT.indexOf(mp), parseInt(dp));
    const diff  = Math.round((dateA - dateB) / (1000 * 60 * 60 * 24));
    return diff < interval ? 'unsched' : 'sched';
  };

  const buildDownloadRows = () => {
    const rows = [];
    vehicleNumbers.forEach(v => {
      const vd = monthData[v] || {};
      Object.entries(vd).forEach(([date, info]) => {
        rows.push({
          Date: date, Time: info.t || '', Vehicle: v,
          Owner: info.owner || '', Driver: info.d || '',
          Litres: info.l || '', Amount: info.a || '',
          PaymentType: info.payType || '', PaidBy: info.p || '',
          FuelStation: info.station || '',
        });
      });
    });
    return rows;
  };

  const downloadCSV = () => {
    const rows = buildDownloadRows();
    const keys = Object.keys(rows[0] || {});
    const lines = [keys.join(','), ...rows.map(r => keys.map(k => `"${r[k] ?? ''}"`).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `sendo_${activeMonth}.csv`; a.click();
  };

  const downloadExcel = () => {
    const rows = buildDownloadRows();
    const loadXLSX = () => {
      const ws = window.XLSX.utils.json_to_sheet(rows);
      const wb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(wb, ws, 'Sendo');
      window.XLSX.writeFile(wb, `sendo_${activeMonth}.xlsx`);
    };
    if (typeof window.XLSX === 'undefined') {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      s.onload = loadXLSX; document.head.appendChild(s);
    } else loadXLSX();
  };

  const downloadPDF = () => alert('PDF export requires jsPDF — integrate as needed.');

  const handleVehicleInput = (val) => {
    setFilterVehicle(val);
    const q = val.toLowerCase();
    setAcMatches(q ? vehicleNumbers.filter(v => v.toLowerCase().includes(q)) : []);
    setAcOpen(true);
  };

  useEffect(() => {
    if (filterMonth && filterYear) {
      const key = `${filterMonth}-${filterYear}`;
      if (key !== activeMonth) { setActiveMonth(key); }
    }
  }, [filterMonth, filterYear]);

  /* White + yellow + black — matches Admin Panel & Vehicle Management */
  const C = {
    bg: '#fff',
    panel: '#fafafa',
    line: '#e0e0e0',
    ink: '#000',
    muted: '#555',
    yellow: '#FFC107',
    green: '#166534',
    red: '#c00',
    barEmpty: '#e0e0e0',
  };
  const S = {
    page:        { marginLeft: typeof window !== 'undefined' && window.innerWidth <= 768 ? 0 : 260, marginRight: 0, fontFamily: 'Arial, sans-serif', backgroundColor: C.bg, color: C.ink, minHeight: 'calc(100vh - 70px)', boxShadow: '0 2px 8px rgba(0,0,0,.08)' },
    heading:     { background: C.yellow, color: C.ink, padding: '14px 24px', fontSize: '1.05rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1, margin: 0 },
    body:        { padding: 16 },
    filterBar:   { background: C.panel, border: '1.5px solid ' + C.ink, borderRadius: 4, padding: '12px 14px', marginBottom: 14 },
    filterRow:   { display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' },
    filterGrp:   { display: 'flex', flexDirection: 'column', gap: 3 },
    filterLbl:   { fontSize: 11, fontWeight: 'bold', color: C.ink, textTransform: 'uppercase', letterSpacing: '.06em' },
    filterInput: { padding: '7px 10px', border: '1.5px solid ' + C.ink, borderRadius: 3, fontSize: 13, outline: 'none', background: C.bg, color: C.ink },
    filterSel:   { padding: '7px 10px', border: '1.5px solid ' + C.ink, borderRadius: 3, fontSize: 13, background: C.bg, outline: 'none', cursor: 'pointer', color: C.ink },
    btnYellow:   { background: C.yellow, color: C.ink, border: '1.5px solid ' + C.yellow, padding: '7px 14px', borderRadius: 3, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 },
    btnBlack:    { background: C.ink, color: '#fff', border: '1.5px solid ' + C.ink, padding: '7px 14px', borderRadius: 3, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 },
    btnWhite:    { background: C.bg, color: C.ink, border: '1.5px solid ' + C.ink, padding: '7px 14px', borderRadius: 3, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 },
    card:        { background: C.bg, border: '1.5px solid ' + C.ink, borderRadius: 4, padding: '14px 16px', marginBottom: 14 },
    cardTitle:   { fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: C.ink, marginBottom: 10 },
    statsRow:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 12, marginBottom: 0 },
    statCard:    { background: C.bg, border: '1.5px solid ' + C.ink, borderRadius: 4, padding: '14px 16px', textAlign: 'left', position: 'relative', overflow: 'hidden' },
    statVal:     { fontFamily: 'monospace', fontSize: '1.25rem', fontWeight: 700, color: C.ink, lineHeight: 1 },
    statLbl:     { fontSize: 11, color: C.muted, marginTop: 6, textTransform: 'uppercase', letterSpacing: '.04em' },
    collHeader:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', cursor: 'pointer', background: C.panel, borderBottom: '1px solid ' + C.line, userSelect: 'none' },
    collTitle:   { fontWeight: 'bold', fontSize: 13, color: C.ink },
    barChart:    { display: 'flex', alignItems: 'flex-end', gap: 3, height: 80, padding: '6px 0', overflowX: 'auto' },
    barCol:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, cursor: 'pointer', minWidth: 22, flexShrink: 0 },
    barVal:      { fontSize: 9, color: C.muted, height: 12, lineHeight: '12px' },
    bar:         { width: 16, borderRadius: '3px 3px 0 0', background: C.ink, transition: 'height .15s', minHeight: 3 },
    barLbl:      { fontSize: 9, color: C.muted, marginTop: 1 },
    dateSelector:{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 0' },
    dateBtn:     (active, hasFills) => ({
      minWidth: 34, height: 34, borderRadius: 3,
      border: `1.5px solid ${active ? C.ink : hasFills ? C.yellow : C.line}`,
      background: active ? C.ink : hasFills ? '#FFF8E1' : 'transparent',
      color: active ? '#fff' : hasFills ? C.ink : '#aaa',
      fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }),
    dayHeader:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: C.panel, borderBottom: '1px solid ' + C.line, flexWrap: 'wrap', gap: 8 },
    dayDate:     { fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: C.ink },
    dayStats:    { display: 'flex', gap: 20, flexWrap: 'wrap' },
    dayStatVal:  { fontFamily: 'monospace', fontWeight: 700, fontSize: 15, color: C.ink },
    dayStatLbl:  { fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: '.05em' },
    mileRow:     { display: 'flex', gap: 10, padding: '12px 14px', borderBottom: '1px solid ' + C.line, flexWrap: 'wrap', alignItems: 'center', background: C.bg },
    mileCard:    { flex: 1, minWidth: 110, background: C.panel, border: '1.5px solid ' + C.ink, borderRadius: 3, padding: '10px', textAlign: 'center', cursor: 'pointer' },
    mileVal:     { fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem', color: C.ink },
    mileLbl:     { fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: C.muted, marginTop: 2 },
    th:          { background: C.ink, color: '#fff', padding: '10px 8px', textAlign: 'center', fontSize: 11, fontWeight: 'bold', whiteSpace: 'nowrap' },
    td:          { border: '1px solid ' + C.line, padding: '8px', textAlign: 'center', fontSize: 12, color: C.ink },
    fillDot:     (cls) => {
      const base = { width: 18, height: 18, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace', fontSize: 9, fontWeight: 700, cursor: 'default', flexShrink: 0 };
      if (cls === 'sched')   return { ...base, background: '#f0fff4', color: C.ink, border: '1px solid ' + C.ink };
      if (cls === 'unsched') return { ...base, background: '#fff0f0', color: C.ink, border: '1px solid ' + C.red };
      if (cls === 'none')    return { ...base, background: '#fffde7', color: C.ink, border: '1px solid ' + C.yellow };
      if (cls === 'missed')  return { ...base, background: '#fff8e1', color: C.ink, border: '1px solid ' + C.yellow };
      return { ...base, background: '#f5f5f5', color: '#aaa', border: '1px solid ' + C.line };
    },
    pvOverlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    pvBox:       { background: C.bg, border: '1.5px solid ' + C.ink, borderRadius: 4, padding: 20, width: 'min(920px,95vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,.2)' },
  };

  const amountDisplay = totalAmount >= 100000
    ? `₹${(totalAmount / 100000).toFixed(1)}L`
    : `₹${Math.round(totalAmount).toLocaleString('en-IN')}`;

  const monthlyStatCards = [
    { icon: '🚛', val: vehicleNumbers.length, lbl: 'Total Vehicles', color: C.yellow },
    { icon: '⛽', val: totalFills, lbl: 'Total Fills', color: C.yellow },
    { icon: '🪣', val: `${Math.round(totalLitres).toLocaleString('en-IN')}L`, lbl: 'Total Litres', color: C.yellow },
    { icon: '₹', val: amountDisplay, lbl: 'Total Amount', color: C.yellow },
    { icon: '📅', val: daysInMon, lbl: 'Days in Month', color: C.yellow },
  ];

  const dayMileage  = selectedDate ? getDayMileage(selectedDate) : { expKm: 0, actKm: 0, litres: 0, kml: null };
  const schedVehs   = getSchedVehicles();
  const filledVehs  = getFilledForDate(schedDate, scheduleData);
  const unfilledVehs= getUnfilledForDate(schedDate, scheduleData);
  const maxBar      = Math.max(...allMonthDates.map(d => dateTotals[d]?.l || 0), 1);

  return (
    <div style={S.page}>
      <h2 style={S.heading}>Admin Dashboard — {MONTHS_FULL[monIdx]} {aYr}</h2>
      <div style={S.body}>

        {/* FILTER BAR */}
        <div style={S.filterBar}>
          <div style={S.filterRow}>

            <div style={{ ...S.filterGrp, position: 'relative' }}>
              <span style={S.filterLbl}>Vehicle</span>
              <div style={{ position: 'relative' }}>
                <input style={{ ...S.filterInput, width: 160 }} placeholder="Search or select..."
                  value={filterVehicle}
                  onChange={e => handleVehicleInput(e.target.value)}
                  onFocus={() => handleVehicleInput(filterVehicle)}
                  onBlur={() => setTimeout(() => setAcOpen(false), 150)} />
                {filterVehicle && (
                  <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: C.muted, fontSize: 12 }}
                    onClick={() => { setFilterVehicle(''); setAcMatches([]); setAcOpen(false); }}>x</span>
                )}
              </div>
              {acOpen && acMatches.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 200, background: C.panel, border: `1px solid ${C.line}`, borderRadius: 8, maxHeight: 180, overflowY: 'auto', minWidth: 180, boxShadow: '0 8px 24px rgba(0,0,0,0.45)', marginTop: 2 }}>
                  {acMatches.map(v => (
                    <div key={v} style={{ padding: '7px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'ui-monospace, monospace', color: C.ink }}
                      onMouseDown={() => { setFilterVehicle(v); setAcOpen(false); }}
                      onMouseEnter={e => { e.target.style.background = 'rgba(255,193,7,0.22)'; }}
                      onMouseLeave={e => { e.target.style.background = ''; }}>
                      {v}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={S.filterGrp}>
              <span style={S.filterLbl}>Vehicle Type</span>
              <select style={S.filterSel} value={filterVType} onChange={e => setFilterVType(e.target.value)}>
                <option value="">All Types</option>
                <option>Pickup Truck</option>
                <option>407 Truck</option>
                <option>17FT</option>
                <option>20FT</option>
                <option>Truck</option>
                <option>Bus</option>
                <option>Car</option>
                <option>Bike</option>
              </select>
            </div>

            <div style={S.filterGrp}>
              <span style={S.filterLbl}>Month</span>
              <select style={S.filterSel} value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
                <option value="">All Months</option>
                {MONTHS_SHORT.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>

            <div style={S.filterGrp}>
              <span style={S.filterLbl}>Year</span>
              <select style={S.filterSel} value={filterYear} onChange={e => setFilterYear(e.target.value)}>
                {['2024','2025','2026','2027'].map(y => <option key={y}>{y}</option>)}
              </select>
            </div>

            <div style={S.filterGrp}>
              <span style={S.filterLbl}>Fill Status</span>
              <select style={S.filterSel} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All</option>
                <option value="active">Active (filled)</option>
                <option value="inactive">Inactive (0 fills)</option>
              </select>
            </div>

            <div style={{ position: 'relative', marginLeft: 'auto' }}>
              <button style={S.btnBlack} onClick={() => setDlOpen(o => !o)}>Download</button>
              {dlOpen && (
                <div style={{ position: 'absolute', right: 0, top: '110%', background: C.panel, border: `1px solid ${C.line}`, borderRadius: 8, zIndex: 300, minWidth: 130, overflow: 'hidden' }}>
                  {[['CSV', downloadCSV], ['Excel', downloadExcel], ['PDF', downloadPDF]].map(([lbl, fn]) => (
                    <button key={lbl} type="button" style={{ display: 'block', width: '100%', padding: '10px 14px', border: 'none', borderBottom: `1px solid ${C.line}`, background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 13, color: C.ink }}
                      onMouseEnter={e => { e.target.style.background = 'rgba(255,193,7,0.22)'; }}
                      onMouseLeave={e => { e.target.style.background = ''; }}
                      onClick={() => { fn(); setDlOpen(false); }}>{lbl}</button>
                  ))}
                </div>
              )}
            </div>

            <button style={S.btnYellow} onClick={() => { setFilterVehicle(''); setFilterVType(''); setFilterMonth(''); setFilterYear(String(now.getFullYear())); setFilterStatus(''); }}>Clear</button>
          </div>

          {(filterVehicle || filterVType || filterStatus) && (
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              {[['vehicle', filterVehicle], ['type', filterVType], ['status', filterStatus]].filter(([,vv]) => vv).map(([k, v]) => (
                <span key={k} style={{ background: 'rgba(245,158,11,0.12)', border: `1px solid ${C.yellow}`, color: C.ink, padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700 }}>
                  {k}: {v} <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.ink, padding: 0, fontSize: 11 }} onClick={() => { if(k==='vehicle') setFilterVehicle(''); if(k==='type') setFilterVType(''); if(k==='status') setFilterStatus(''); }}>x</button>
                </span>
              ))}
            </div>
          )}
        </div>

        {!fleetLoading && fleetRecords.length === 0 && API_BASE_URL && (
          <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 10, border: `1px solid ${C.line}`, background: 'rgba(245,158,11,0.08)', color: C.yellow, fontSize: 12 }}>
            No vehicles returned from Vehicle Onboarding. Add vehicles under Vehicle Management → Vehicle Onboarding so counts and types stay in sync. Diesel history may still reference vehicles by number before onboarding.
          </div>
        )}
        {/* No data */}
        {!loading && !hasAnyData && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: C.muted, border: `1px dashed ${C.line}`, borderRadius: 14, marginBottom: 14, background: C.bg }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6, color: C.ink }}>No Data for {MONTHS_FULL[monIdx]} {aYr}</div>
            <div style={{ fontSize: 13 }}>No diesel fill records for this period. Import data or choose another month.</div>
          </div>
        )}

        {/* SCHEDULE STATUS — matches HTML prototype */}
        <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px', borderBottom: `1px solid ${C.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ ...S.cardTitle, marginBottom: 4 }}>📋 Schedule Status</div>
              <div style={{ fontSize: '0.82rem', color: C.muted }}>
                📅 {schedDate ? new Date(`${schedDate}T12:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'}
              </div>
            </div>
            <input type="date" value={schedDate} onChange={e => setSchedDate(e.target.value)}
              style={{ ...S.filterInput, fontSize: '0.82rem' }} />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'space-around', flexWrap: 'wrap', background: C.panel, borderRadius: 10, padding: '14px', margin: '0 16px 16px' }}>
            {[
              { lbl: 'Scheduled', val: schedVehs.length, id: 'sched', lblColor: C.ink, valColor: C.ink },
              { lbl: 'Filled', val: filledVehs.length, id: 'filled', lblColor: C.green, valColor: C.green },
              { lbl: 'Unfilled', val: unfilledVehs.length, id: 'unfilled', lblColor: C.red, valColor: C.red },
            ].map(({ lbl, val, id, lblColor, valColor }) => (
              <div key={id} style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setSchedPopup(p => p === id ? '' : id)}>
                <div style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: lblColor, marginBottom: 6 }}>{lbl}</div>
                <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '1.4rem', fontWeight: 700, color: valColor }}>{val}</div>
              </div>
            ))}
          </div>

          {schedPopup && (
            <div style={{ padding: '10px 16px 16px', display: 'flex', flexWrap: 'wrap', gap: 4, borderTop: `1px solid ${C.line}` }}>
              {(schedPopup === 'sched' ? schedVehs : schedPopup === 'filled' ? filledVehs : unfilledVehs).map(v => (
                <span key={v} style={{
                  display: 'inline-block', padding: '3px 10px', borderRadius: 6,
                  fontFamily: 'ui-monospace, monospace', fontSize: '0.75rem', fontWeight: 600, margin: 2,
                  background: schedPopup === 'filled' ? 'rgba(34,197,94,0.1)' : schedPopup === 'unfilled' ? 'rgba(248,113,113,0.1)' : 'rgba(100,116,139,0.15)',
                  color: schedPopup === 'filled' ? C.green : schedPopup === 'unfilled' ? C.red : C.ink,
                  border: `1px solid ${schedPopup === 'filled' ? 'rgba(34,197,94,0.25)' : schedPopup === 'unfilled' ? 'rgba(248,113,113,0.25)' : C.line}`,
                }}>{v}</span>
              ))}
              {((schedPopup === 'filled' && filledVehs.length === 0) || (schedPopup === 'unfilled' && unfilledVehs.length === 0)) && (
                <span style={{ color: C.muted, fontSize: 12 }}>{schedPopup === 'unfilled' ? 'All filled' : 'None'}</span>
              )}
            </div>
          )}
        </div>

        {/* MONTHLY SUMMARY — stat cards + vehicles overview */}
        <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 16px 8px' }}>
            <div style={{ ...S.cardTitle, marginBottom: 12 }}>📊 Monthly Summary</div>
            <div style={{ fontSize: '0.8rem', color: C.muted, marginBottom: 14 }}>
              {MONTHS_FULL[monIdx]} {aYr} — {vehicleNumbers.length} vehicle(s) from Vehicle Management
              {driversList.length > 0 ? ` · ${driversList.length} driver(s) in Driver Management` : ''}
              {fleetLoading ? ' · Loading fleet…' : ''}
            </div>
            <div style={S.statsRow}>
              {monthlyStatCards.map(s => (
                <div key={s.lbl} style={{ ...S.statCard, borderTop: `3px solid ${s.color}` }}>
                  <div style={{ fontSize: '1.25rem', marginBottom: 8, lineHeight: 1 }}>{s.icon}</div>
                  <div style={S.statVal}>{s.val}</div>
                  <div style={S.statLbl}>{s.lbl}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ overflowX: 'auto', borderTop: `1px solid ${C.line}` }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {['Vehicle','Type','Fill Days','Total Litres','Total Amount','Avg L/Fill'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vehicleNumbers.filter(v => Object.keys(monthData[v] || {}).length > 0).map((v, i) => {
                  const vd = monthData[v] || {};
                  const fills = Object.keys(vd).length;
                  const l = Object.values(vd).reduce((s, d) => s + (d.l || 0), 0);
                  const a = Object.values(vd).reduce((s, d) => s + (d.a || 0), 0);
                  return (
                    <tr key={v} style={{ background: i % 2 === 0 ? C.bg : C.panel }}>
                      <td style={{ ...S.td, fontFamily: 'ui-monospace, monospace', fontWeight: 700, textAlign: 'left' }}>{v}</td>
                      <td style={S.td}>{getVehicleTypeDisplay(v) || '—'}</td>
                      <td style={{ ...S.td, fontWeight: 700 }}>{fills}</td>
                      <td style={{ ...S.td, fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>{Math.round(l)}L</td>
                      <td style={{ ...S.td, fontFamily: 'ui-monospace, monospace' }}>₹{a.toLocaleString('en-IN')}</td>
                      <td style={S.td}>{fills > 0 ? (l / fills).toFixed(1) + 'L' : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* DAILY ACTIVITY — chart, date strip, mileage, grid (prototype-aligned) */}
        <div style={{ ...S.card, padding: 0, overflow: 'hidden', marginBottom: 14 }}>
          <div style={S.collHeader} onClick={() => setDailyCollapsed(c => !c)}>
            <span style={S.collTitle}>📋 Daily View</span>
            <span style={{ fontSize: 12, color: C.muted }}>{dailyCollapsed ? '▶' : '▼'}</span>
          </div>

          {!dailyCollapsed && (
            <>
              <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.line}`, background: C.bg }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.06em' }}>📊 Daily Total Litres — Click a bar to see that day</div>
                <div style={S.barChart}>
                  {allMonthDates.map(d => {
                    const l    = dateTotals[d]?.l || 0;
                    const pct  = l / maxBar;
                    const isActive = d === selectedDate;
                    const hasData  = l > 0;
                    return (
                      <div key={d} style={S.barCol} onClick={() => setSelectedDate(d)}>
                        <div style={S.barVal}>{l > 0 ? Math.round(l) : ''}</div>
                        <div style={{
                          ...S.bar,
                          height: `${Math.max(3, pct * 70)}px`,
                          background: isActive ? C.yellow : hasData ? C.ink : C.barEmpty,
                          opacity: hasData || isActive ? 1 : 0.35,
                        }} />
                        <div style={S.barLbl}>{d.split('-')[0]}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ padding: '8px 16px', borderBottom: `1px solid ${C.line}`, background: C.bg }}>
                <div style={S.dateSelector}>
                  {allMonthDates.map(d => {
                    const hasFills = (dateTotals[d]?.count || 0) > 0;
                    const isActive = d === selectedDate;
                    return (
                      <button key={d} style={S.dateBtn(isActive, hasFills)} onClick={() => setSelectedDate(d)}>
                        {d.split('-')[0]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedDate && (
                <div style={S.dayHeader}>
                  <div style={S.dayDate}>
                    {parseInt(selectedDate.split('-')[0])} {MONTHS_FULL[MONTHS_SHORT.indexOf(selectedDate.split('-')[1])]} {aYr}
                  </div>
                  <div style={S.dayStats}>
                    {[
                      { val: dateTotals[selectedDate]?.count || '—', lbl: 'Vehicles Filled' },
                      { val: dateTotals[selectedDate]?.l ? Math.round(dateTotals[selectedDate].l) + 'L' : '—', lbl: 'Total Litres' },
                      { val: dateTotals[selectedDate]?.a ? '₹' + dateTotals[selectedDate].a.toLocaleString('en-IN') : '—', lbl: 'Total Amount' },
                      { val: dateTotals[selectedDate]?.count ? vehicleNumbers.length - dateTotals[selectedDate].count : '—', lbl: 'Not Filled' },
                    ].map(s => (
                      <div key={s.lbl} style={{ textAlign: 'center' }}>
                        <div style={S.dayStatVal}>{s.val}</div>
                        <div style={S.dayStatLbl}>{s.lbl}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={S.mileRow}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: C.muted, marginRight: 6, whiteSpace: 'nowrap' }}>
                  📍 Mileage — {selectedDate || '—'}
                </div>
                {[
                  { lbl: 'Expected KM', val: dayMileage.expKm > 0 ? dayMileage.expKm.toLocaleString('en-IN') + ' km' : '—', valColor: C.yellow },
                  { lbl: 'Actual KM',   val: dayMileage.actKm > 0 ? dayMileage.actKm.toLocaleString('en-IN') + ' km' : '—', valColor: C.green },
                  { lbl: 'Avg KM/L',    val: dayMileage.kml   ? dayMileage.kml + ' km/L' : '—', valColor: C.yellow },
                ].map(m => (
                  <div key={m.lbl} style={S.mileCard} onClick={() => setPvmileOpen(true)}>
                    <div style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '.06em', color: m.valColor, marginBottom: 4, fontWeight: 700 }}>{m.lbl}</div>
                    <div style={{ ...S.mileVal, color: m.valColor }}>{m.val}</div>
                  </div>
                ))}
                <button type="button" onClick={() => setPvmileOpen(true)}
                  style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.35)', color: '#a5b4fc', borderRadius: 8, padding: '6px 12px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                  🚛 Per Vehicle →
                </button>
              </div>

              {selectedDate && (dateTotals[selectedDate]?.count || 0) > 0 ? (
                <div style={{ overflowX: 'auto', maxHeight: '50vh', overflowY: 'auto', background: C.bg }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1100 }}>
                    <thead>
                      <tr>
                        {['#','Vehicle No','Driver','Owner Name','Paid By','Fuel Station','Ltrs','Amount','Time','Payment Type','Sched KM/L','Exp KM','Opening KM','Closing KM','Total KM','Difference','Actual KM/L','Status'].map(h => (
                          <th key={h} style={S.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {vehicleNumbers.map((v, i) => {
                        const info = (monthData[v] || {})[selectedDate];
                        if (!info) return (
                          <tr key={v} style={{ background: C.panel }}>
                            <td style={{ ...S.td, color: C.muted, fontSize: 10 }}>{i + 1}</td>
                            <td style={{ ...S.td, fontFamily: 'ui-monospace, monospace', fontWeight: 700, textAlign: 'left' }}>{v}</td>
                            <td style={{ ...S.td, color: C.muted }} colSpan={16}>No fill today</td>
                            <td style={S.td}><span style={{ background: 'rgba(248,113,113,0.12)', color: C.red, padding: '2px 8px', borderRadius: 6, fontSize: 11, border: '1px solid rgba(248,113,113,0.35)' }}>Not Filled</span></td>
                          </tr>
                        );
                        const sc = effectiveScheduleConfig[v];
                        return (
                          <tr key={v} style={{ background: i % 2 === 0 ? C.bg : C.panel }}>
                            <td style={{ ...S.td, color: C.muted, fontSize: 10 }}>{i + 1}</td>
                            <td style={{ ...S.td, fontFamily: 'ui-monospace, monospace', fontWeight: 700, textAlign: 'left' }}>{v}</td>
                            <td style={S.td}>{info.d || '—'}</td>
                            <td style={S.td}>{info.owner || '—'}</td>
                            <td style={S.td}>{info.p || '—'}</td>
                            <td style={S.td}>{info.station || '—'}</td>
                            <td style={{ ...S.td, fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>{info.l}L</td>
                            <td style={{ ...S.td, fontFamily: 'ui-monospace, monospace' }}>₹{(info.a || 0).toLocaleString('en-IN')}</td>
                            <td style={S.td}>{info.t || '—'}</td>
                            <td style={S.td}>{info.payType || '—'}</td>
                            <td style={S.td}>{sc?.kmPerLitre ? sc.kmPerLitre + ' km/L' : '—'}</td>
                            <td style={S.td}>{sc?.kmPerLitre && info.l ? Math.round(sc.kmPerLitre * info.l) + ' km' : '—'}</td>
                            <td style={S.td}>{info.openKm != null && info.openKm !== '' ? Number(info.openKm).toLocaleString('en-IN') : '—'}</td>
                            <td style={S.td}>{info.closeKm != null && info.closeKm !== '' ? Number(info.closeKm).toLocaleString('en-IN') : '—'}</td>
                            <td style={S.td}>{info.runKm != null && info.runKm !== '' ? Number(info.runKm).toLocaleString('en-IN') : '—'}</td>
                            <td style={S.td}>—</td>
                            <td style={S.td}>—</td>
                            <td style={S.td}><span style={{ background: 'rgba(34,197,94,0.12)', color: C.green, padding: '2px 8px', borderRadius: 6, fontSize: 11, border: '1px solid rgba(34,197,94,0.35)' }}>Filled</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : selectedDate && (
                <div style={{ textAlign: 'center', padding: '30px 20px', color: C.muted, fontSize: 13, background: C.bg }}>
                  No diesel fills recorded for {selectedDate}
                </div>
              )}
            </>
          )}
        </div>

        {/* TPS VEHICLE SUMMARY TABLE */}
        <div style={{ ...S.card, padding: 0, overflow: 'hidden', marginBottom: 14 }}>
          <div style={S.collHeader} onClick={() => setTpsCollapsed(c => !c)}>
            <span style={S.collTitle}>Vehicle Summary Table <span style={{ fontSize: 11, fontWeight: 400, color: C.muted }}>— {sortedTpsVehicles.length} vehicles</span></span>
            <span style={{ fontSize: 12, color: C.muted }}>{tpsCollapsed ? '▶' : '▼'}</span>
          </div>

          {!tpsCollapsed && (
            <>
              <div style={{ display: 'flex', gap: 12, padding: '10px 14px', borderBottom: `1px solid ${C.line}`, flexWrap: 'wrap', fontSize: 11, color: C.muted, background: C.bg }}>
                {[
                  { bg: 'rgba(34,197,94,0.15)', border: '#166534',  label: 'On schedule'      },
                  { bg: 'rgba(248,113,113,0.12)', border: C.red,  label: 'Filled too early' },
                  { bg: 'rgba(245,158,11,0.12)', border: C.yellow,label: 'No schedule'     },
                  { bg: 'rgba(245,158,11,0.18)', border: C.yellow,label: 'Ltrs mismatch'   },
                ].map(l => (
                  <span key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: l.bg, border: `1px solid ${l.border}` }} />
                    {l.label}
                  </span>
                ))}
              </div>

              <div style={{ padding: '8px 14px', borderBottom: `1px solid ${C.line}`, background: C.bg }}>
                <input style={{ ...S.filterInput, width: 220 }} placeholder="Search vehicle..."
                  value={tpsSearch} onChange={e => setTpsSearch(e.target.value)} />
              </div>

              <div style={{ overflowX: 'auto', background: C.bg }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {[
                        ['vehicle','Vehicle'],['vtype','Type'],['fills','Fill Days'],
                        ['litres','Total Litres'],['amount','Total Amount'],['avg','Avg L/Fill'],
                        [null,'Fill Day Calendar'],
                      ].map(([col, lbl]) => (
                        <th key={lbl}
                          style={{ ...S.th, cursor: col ? 'pointer' : 'default' }}
                          onClick={col ? () => handleTpsSort(col) : undefined}>
                          {lbl}{col && (tpsSortCol === col ? (tpsSortDir === 'asc' ? ' asc' : ' desc') : '')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTpsVehicles.length === 0 ? (
                      <tr><td colSpan={7} style={{ ...S.td, color: C.muted, padding: 40, textAlign: 'center' }}>No vehicles match filters</td></tr>
                    ) : sortedTpsVehicles.map((v, i) => {
                      const vd    = monthData[v] || {};
                      const fills = Object.keys(vd).length;
                      const l     = Object.values(vd).reduce((s, d) => s + (d.l || 0), 0);
                      const a     = Object.values(vd).reduce((s, d) => s + (d.a || 0), 0);
                      const avg   = fills > 0 ? (l / fills).toFixed(1) : '—';
                      const sc    = effectiveScheduleConfig[v];
                      const hasSchedule = !!sc?.interval;

                      return (
                        <tr key={v} style={{ background: i % 2 === 0 ? C.bg : C.panel }}>
                          <td style={{ ...S.td, textAlign: 'left' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, color: C.ink, cursor: 'pointer', whiteSpace: 'nowrap' }}>{v}</span>
                              <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer', background: hasSchedule ? 'rgba(245,158,11,0.12)' : C.panel, color: C.ink, border: `1px solid ${hasSchedule ? C.yellow : C.line}` }}>
                                {hasSchedule ? `${sc.interval}d` : '+ Schedule'}
                              </span>
                            </div>
                          </td>
                          <td style={S.td}>
                            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: C.panel, color: C.ink, border: `1px solid ${C.line}`, whiteSpace: 'nowrap' }}>
                              {getVehicleTypeDisplay(v) || 'Fleet'}
                            </span>
                          </td>
                          <td style={{ ...S.td, fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>{fills}</td>
                          <td style={{ ...S.td, fontFamily: 'ui-monospace, monospace' }}>{Math.round(l).toLocaleString('en-IN')}L</td>
                          <td style={{ ...S.td, fontFamily: 'ui-monospace, monospace' }}>₹{a.toLocaleString('en-IN')}</td>
                          <td style={{ ...S.td, fontFamily: 'ui-monospace, monospace' }}>{avg}L</td>
                          <td style={S.td}>
                            <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', maxWidth: 380 }}>
                              {allMonthDates.map(d => {
                                const isFilled = !!(vd[d]);
                                if (!isFilled) {
                                  return <div key={d} style={S.fillDot('empty')} title={`${d}: No fill`}>{d.split('-')[0]}</div>;
                                }
                                const cls = !hasSchedule ? 'none' : classifyFillDay(v, d) === 'unsched' ? 'unsched' : 'sched';
                                const info = vd[d];
                                const sl = sc?.litresPerFill ? parseFloat(sc.litresPerFill) : null;
                                const fl = parseFloat(info.l);
                                const dotCls = sl ? (Math.abs(fl - sl) < 0.5 ? 'sched' : 'unsched') : cls;
                                return (
                                  <div key={d} style={S.fillDot(dotCls)}
                                    title={`${d}: ${info.l}L — Rs.${(info.a||0).toLocaleString()}${info.t ? ' · ' + info.t : ''}`}>
                                    {d.split('-')[0]}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

      </div>

      {/* PER-VEHICLE MILEAGE POPUP */}
      {pvmileOpen && (
        <div style={S.pvOverlay} onClick={e => e.target === e.currentTarget && setPvmileOpen(false)}>
          <div style={S.pvBox}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexShrink: 0, gap: 8, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: C.muted }}>
                🚛 Per-Vehicle Mileage — <span style={{ color: C.ink, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{activeMonth}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input style={{ ...S.filterInput, width: 130, fontSize: 12 }} placeholder="Search vehicle..."
                  value={pvmileSearch} onChange={e => setPvmileSearch(e.target.value)} />
                <button type="button" onClick={() => setPvmileOpen(false)}
                  style={{ background: C.panel, border: `1px solid ${C.line}`, color: C.ink, borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}>
                  ✕ Close
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 14, fontSize: 11, marginBottom: 8, flexShrink: 0, flexWrap: 'wrap', color: C.muted }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'rgba(34,197,94,0.15)', border: '1px solid #166534', marginRight: 4 }} />Fill only</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'rgba(255,193,7,0.25)', border: `1px solid ${C.yellow}`, marginRight: 4 }} />Has odometer</span>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {vehicleNumbers
                .filter(v => {
                  if (pvmileSearch && !v.toLowerCase().includes(pvmileSearch.toLowerCase())) return false;
                  return Object.keys(monthData[v] || {}).length > 0;
                })
                .map(v => {
                  const vd  = monthData[v] || {};
                  const sc  = effectiveScheduleConfig[v];
                  let monExp = 0;

                  return (
                    <div key={v} style={{ marginBottom: 10, background: C.panel, border: `1px solid ${C.line}`, borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                        <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, fontWeight: 700, color: C.ink }}>{v}</span>
                        <div style={{ display: 'flex', gap: 10, fontSize: 11, fontFamily: 'ui-monospace, monospace', flexWrap: 'wrap', color: C.ink }}>
                          {(() => {
                            const l = Object.values(vd).reduce((s, d) => s + (d.l || 0), 0);
                            if (sc?.kmPerLitre) monExp = Math.round(sc.kmPerLitre * l);
                            return (
                              <>
                                <span>Exp: {monExp > 0 ? monExp.toLocaleString() + ' km' : '—'}</span>
                                <span>Act: —</span>
                                <span>KM/L: {sc?.kmPerLitre ? sc.kmPerLitre : '—'}</span>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {allMonthDates.map(date => {
                          const info = vd[date];
                          const dayNum = date.split('-')[0];
                          if (!info) return (
                            <div key={date} style={{ width: 36, height: 36, borderRadius: 6, border: `1px solid ${C.line}`, background: C.panel, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: C.muted, fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>
                              {dayNum}
                            </div>
                          );
                          return (
                            <div key={date} style={{ width: 36, height: 36, borderRadius: 6, border: '1px solid #166534', background: 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: C.green, fontFamily: 'ui-monospace, monospace', fontWeight: 700, cursor: 'pointer' }}
                              title={`${date}: ${info.l}L — ₹${(info.a||0).toLocaleString('en-IN')}${info.d ? ' · ' + info.d : ''}`}>
                              {dayNum}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              {vehicleNumbers.filter(v => Object.keys(monthData[v] || {}).length > 0).length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: C.muted, fontSize: 13 }}>No data for this month</div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}